import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import { JobGradeFormRule, JobGradeFormSchema } from "@core/form/form.rule";
import { useFormDefaultJobGrade } from "@core/form/form-default-job-grade.hook";
import { useFormApiMapper } from "@core/services/form-api-mapper.hook";
import { ActionEnum } from "@core/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { 
  useQyUpdateJobGrade, 
  useQyGetJobGradeById, 
  useQyDeleteJobGrade 
} from "@core/query/job-grade.query";
import { GetResponseJobGradeModel } from "@core/model/query-model-job-grade";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";

export function useEditJobGrade() {
  // LOCAL DECLARATION
  const { getDefault } = useFormDefaultJobGrade();
  const navigate = useNavigate();
  const { mapUpdateJobGrade, mapDeleteJobGrade } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { jobGradeId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID JobGrade
  const handleGetApiSuccess = (data: GetResponseJobGradeModel) => {
    if (data && data.count && data.count > 0) {
      const responseData = data.data?.[0];
      setValue("name", responseData?.name || "");
      setValue("description", responseData?.description || "");
    }
  };
  const {
    data: jobGradeResponse,
    isLoading,
    isError: jobGradeError,
  } = useQyGetJobGradeById(jobGradeId || "", handleGetApiSuccess);
  const jobGradeData = jobGradeResponse?.data?.[0];

  // API UPDATE JobGrade
  const handleUpdateApiSuccess = () => {
    showSuccess("JobGrade updated successfully");
  };
  const { mutate: updateJobGrade, isLoading: isEditLoading } = useQyUpdateJobGrade(
    handleUpdateApiSuccess
  );

  // API DELETE JobGrade
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("JobGrade deleted successfully");
  };
  const { mutate: deleteJobGrade } = useQyDeleteJobGrade(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<JobGradeFormSchema>({
    defaultValues: getDefault(jobGradeData),
    resolver: zodResolver(JobGradeFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: JobGradeFormSchema) => {
    if (!jobGradeData) {
      throw new Error("No jobGrade data");
    }

    const formData = mapUpdateJobGrade(form, jobGradeData.code);
    updateJobGrade(formData);
  };
  const handleValidateError = (err: FieldErrors<JobGradeFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!jobGradeData) {
      throw new Error("No jobGrade data found");
    }

    const form = mapDeleteJobGrade(jobGradeData.code);
    deleteJobGrade(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    jobGradeId,
    jobGradeData,
    isLoading,
    jobGradeError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}